import React from 'react';
import { useFormContext } from 'react-hook-form';
import { LenderMappingsFormValues } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { CurrencyInputField, InputField, NumberFormatField } from 'components/form';
import { LenderFieldMap } from '@trnsact/trnsact-shared-types';

interface Props {
  field: LenderFieldMap;
  inputProps: any;
  formatter?: (value: string) => string;
  onValueChange: (lenderProperty: string, newValue: any) => void;
}

export const TextMappingField = ({ field, inputProps, formatter, onValueChange }: Props) => {
  const { control } = useFormContext<LenderMappingsFormValues>();

  const type = field.details?.fieldInputType;
  const isRequired = field.details?.required === 'REQUIRED';
  const fieldName = `mappingsByLenderFieldId.${field.lenderFieldId}.mappedValue` as const;
  const rules = { required: isRequired };
  const minMaxProps = {
    ...(inputProps.min && { min: inputProps.min }),
    ...(inputProps.max && { max: inputProps.max }),
  };

  const hasError = !!field.details?.errors?.length;
  const errorMessages = field.details?.errors?.map(e => e.message).join(', ');
  const label = field.details?.displayName ?? '';

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (field.lenderProperty) onValueChange(field.lenderProperty, e.target.value);
  };

  if (type === 'FLOAT' || type === 'INTEGER') {
    return (
      <NumberFormatField
        label={label}
        control={control}
        name={fieldName}
        rules={rules}
        textFieldProps={{
          error: hasError,
          required: isRequired,
          variant: 'outlined',
          size: 'small',
          helperText: errorMessages,
          onBlur: handleBlur,
          inputProps: minMaxProps,
        }}
      />
    );
  }

  if (type === 'MONEY') {
    return (
      <CurrencyInputField
        label={label}
        control={control}
        name={fieldName}
        rules={rules}
        textFieldProps={{
          error: hasError,
          required: isRequired,
          variant: 'outlined',
          helperText: errorMessages,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            if (field.lenderProperty) {
              onValueChange(field.lenderProperty, e.target.value.replace(/[^0-9.-]+/g, ''));
            }
          },
          inputProps: minMaxProps,
        }}
      />
    );
  }

  return (
    <InputField
      label={label}
      control={control}
      name={fieldName}
      rules={rules}
      formatter={formatter}
      inputProps={{
        error: hasError,
        required: isRequired,
        variant: 'outlined',
        helperText: errorMessages,
        onBlur: handleBlur,
        ...minMaxProps,
      }}
    />
  );
};
